import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import {
  RESOURCE_PICKERS,
  type ResourcePickerRow,
} from "@/components/messaging/resourcePickers";
import { ownerTypeLabel } from "@/api/communications";

/**
 * Communication-log placement picker. Reuses the messaging service's
 * resource-picker registry (Internal Messaging Integration Standard §6.3)
 * so additional placements on a comm-log entry can be selected by
 * search-and-pick instead of by pasting a Guid into a free-text input.
 *
 * Owner types whose modules have no SPA picker yet (loan, vendor, spv,
 * investor, candidate per <c>RESOURCE_PICKERS</c>) fall back to the legacy
 * ID-paste input — those entities don't have detail pages either, so the
 * paste path is the only way to reference them in v1.
 */
export function PlacementOwnerPicker({
  ownerType,
  ownerId,
  ownerLabel,
  disabled,
  ariaLabel,
  onChange,
}: {
  ownerType: string;
  ownerId: string;
  ownerLabel?: string;
  disabled?: boolean;
  ariaLabel?: string;
  onChange: (ownerId: string, label?: string) => void;
}) {
  const adapter = RESOURCE_PICKERS[ownerType];
  const [open, setOpen] = useState(false);

  // No registered picker → fall back to a raw ID input. Keeps loan / vendor /
  // spv / investor / candidate functional until their modules ship.
  if (!adapter) {
    return (
      <Input
        value={ownerId}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ID (paste from URL or record)"
        disabled={disabled}
        aria-label={ariaLabel}
      />
    );
  }

  const display = ownerLabel?.trim() || ownerId;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          "h-10 w-full inline-flex items-center justify-between gap-space-2",
          "px-space-3 rounded-md border border-border-light bg-white-pure",
          "text-small text-onyx text-left",
          "hover:border-gold focus:outline-none focus:border-gold focus:shadow-goldRing",
          "disabled:opacity-60 disabled:cursor-not-allowed",
        )}
      >
        <span className={cn("truncate", !display && "text-gray-mid")}>
          {display || `Select ${ownerTypeLabel(ownerType).toLowerCase()}…`}
        </span>
        <Search size={14} strokeWidth={1.5} className="text-gray-mid shrink-0" aria-hidden />
      </button>

      <PlacementPickerModal
        open={open}
        ownerType={ownerType}
        onClose={() => setOpen(false)}
        onPick={(row) => {
          onChange(row.id, row.primary);
          setOpen(false);
        }}
      />
    </>
  );
}

function PlacementPickerModal({
  open,
  ownerType,
  onClose,
  onPick,
}: {
  open: boolean;
  ownerType: string;
  onClose: () => void;
  onPick: (row: ResourcePickerRow) => void;
}) {
  const adapter = RESOURCE_PICKERS[ownerType];
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const rows = useQuery({
    queryKey: ["communications", "placement-picker", ownerType, q],
    queryFn: () => adapter!.search(q),
    enabled: open && !!adapter,
    staleTime: 30_000,
  });

  const typeLabel = useMemo(
    () => adapter?.typeLabel ?? ownerTypeLabel(ownerType),
    [adapter, ownerType],
  );

  return (
    <Modal open={open} onClose={onClose} dismissOnBackdropClick={false} title={`Pick ${typeLabel.toLowerCase()}`} size="md">
      <div className="relative">
        <Search
          size={16}
          strokeWidth={1.5}
          className="absolute left-space-3 top-1/2 -translate-y-1/2 text-gray-mid"
          aria-hidden
        />
        <input
          autoFocus
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={adapter?.placeholder ?? "Search…"}
          aria-label={`Search ${typeLabel.toLowerCase()}`}
          className={cn(
            "w-full h-10 pl-10 pr-space-3 rounded-md font-body text-small",
            "bg-white-pure text-onyx border border-border-light",
            "focus:outline-none focus:border-gold focus:shadow-goldRing",
          )}
        />
      </div>

      <ul className="mt-space-3 m-0 p-0 list-none max-h-[50vh] overflow-y-auto">
        {rows.isLoading && (
          <li className="text-small text-gray-mid p-space-3">Searching…</li>
        )}
        {!rows.isLoading && (rows.data?.length ?? 0) === 0 && (
          <li className="text-small text-gray-mid p-space-3">
            No {typeLabel.toLowerCase()} found.
          </li>
        )}
        {rows.data?.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onPick(row)}
              className={cn(
                "w-full text-left px-space-3 py-space-2 rounded-md",
                "hover:bg-white-warm focus-visible:outline-none focus-visible:bg-white-warm",
              )}
            >
              <div className="font-display font-semibold text-small text-onyx">
                {row.primary}
              </div>
              {row.secondary && (
                <div className="text-label text-gray-mid">{row.secondary}</div>
              )}
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
